import {
  fetchBaseQuery,
  type BaseQueryFn,
  type FetchArgs,
  type FetchBaseQueryError,
} from '@reduxjs/toolkit/query/react';
import { Mutex } from 'async-mutex';
import { env } from '@/lib/env';
import { resolveActiveTenantHeader } from '@/lib/activeTenantStorage';
import { tokenStorage } from '@/lib/tokens';
import { authActions } from '@/store/slices/authSlice';
import type { LoginResponse } from '@/types/auth';
import type { RootState } from '@/store/store';

const mutex = new Mutex();

export const rawBaseQuery = fetchBaseQuery({
  baseUrl: `${env.VITE_API_BASE_URL}/api/v1`,
  prepareHeaders(headers, { getState }) {
    const token = tokenStorage.getAccess();
    if (token) {
      headers.set('Authorization', `Bearer ${token}`);
    }
    const state = getState() as RootState;
    const userId = state.auth.me?.id;
    const tenantHeader = resolveActiveTenantHeader(userId);
    if (tenantHeader) {
      headers.set('x-tenant-id', tenantHeader);
    }
    return headers;
  },
});

export const baseQueryWithReauth: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> = async (
  args,
  api,
  extraOptions,
) => {
  await mutex.waitForUnlock();
  let result = await rawBaseQuery(args, api, extraOptions);

  if (result.error?.status === 401) {
    if (!mutex.isLocked()) {
      const release = await mutex.acquire();
      try {
        const refresh = tokenStorage.getRefresh();
        if (refresh) {
          const refreshResult = await rawBaseQuery(
            {
              url: '/auth/refresh',
              method: 'POST',
              body: { refreshToken: refresh },
            },
            api,
            extraOptions,
          );
          const refreshed = refreshResult.data as LoginResponse | undefined;
          if (refreshed?.accessToken && refreshed.refreshToken) {
            tokenStorage.set(refreshed.accessToken, refreshed.refreshToken);
            result = await rawBaseQuery(args, api, extraOptions);
          } else {
            api.dispatch(authActions.logout());
            tokenStorage.clear();
          }
        } else {
          api.dispatch(authActions.logout());
          tokenStorage.clear();
        }
      } finally {
        release();
      }
    } else {
      await mutex.waitForUnlock();
      result = await rawBaseQuery(args, api, extraOptions);
    }
  }

  return result;
};

export { mutex };
